import React, { useState } from 'react';
import { Alert, Box, Button, Link, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import type { ComparableCohortPublic } from '../api/constructionPriceEstimatorFunctions';
import { date, displayBackendValue, displayUserMessages, money, sourceLabel, textList } from './ConstructionPriceSupportingEvidence';
import { canonicalAwardKey, permittedUSAspendingUrl, usaSpendingRetryMessage, usaspendingSourceRun, type SourceRun } from './ConstructionPriceResearch';
import { ExternalField, ExternalTextarea } from './ConstructionPriceContractDetails';

const PUBLIC_MATCH_FACTORS: Record<string, string> = {
  psc: 'PSC exact match',
  naics: 'NAICS exact match',
  location: 'Project-state exact match',
  date: 'Action date within five-year window',
  size_driver: 'Known size driver and unit match',
};

const comparableBasis = (item: Record<string, any>): string => {
  // Backend rationales include a deterministic score, and query_basis names
  // the server policy phase. Only render known public match-factor labels.
  const factors = Array.isArray(item.matched_factors) && item.matched_factors.length
    ? item.matched_factors
    : Array.isArray(item.match_basis) ? item.match_basis
      : [item.match_basis, item.match_rationale]
        .filter((value): value is string => typeof value === 'string')
        .flatMap((value) => value.split(/[;·]/).map((part) => part.trim()));
  const labels = factors
    .map((factor: unknown) => typeof factor === 'string'
      ? PUBLIC_MATCH_FACTORS[factor] ?? Object.values(PUBLIC_MATCH_FACTORS).find((label) => label === factor)
      : undefined)
    .filter((label): label is string => Boolean(label));
  return Array.from(new Set(labels)).join(' · ') || 'No match basis reported';
};

interface HistoricalAwardComparablesProps {
  items: Record<string, any>[];
  sourceRuns: SourceRun[];
  comparables?: Record<string, any>;
  warnings: string[];
  isRom: boolean;
  canEdit: boolean;
  saving: boolean;
  rationales: Record<string, string>;
  onRationaleChange: (awardId: string, value: string) => void;
  onExclude: (awardId: string) => void;
  comparableCohort?: ComparableCohortPublic | null;
  awardEvidenceIsContextOnly?: boolean;
  embedded?: boolean;
}

export const HistoricalAwardComparables: React.FC<HistoricalAwardComparablesProps> = ({
  items,
  sourceRuns,
  comparables,
  warnings,
  isRom,
  canEdit,
  saving,
  rationales,
  onRationaleChange,
  onExclude,
  comparableCohort,
  awardEvidenceIsContextOnly = false,
  embedded = false,
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const run = usaspendingSourceRun(sourceRuns);
  const status = String(run?.status ?? '').toLowerCase();
  const retryMessage = usaSpendingRetryMessage(run);
  const failed = Boolean(retryMessage) || status === 'not_authorized';
  // A no-results message is only truthful after the source completed its
  // search. Missing or unavailable run data must not masquerade as a
  // completed empty search.
  // A multi-phase USAspending search may end with an empty fallback query
  // after an earlier phase already returned public award rows. Rows always
  // take precedence over a single phase's no-results status.
  const noResults = items.length === 0
    && (status === 'no_results' || status === 'current' || status === 'complete');
  // The selected cohort is used for price math, but the disclosure retains
  // every public candidate so an owner can understand the result.
  const qualifyingItems = items.filter((item) => item.included !== false);
  const includedCount = qualifyingItems.length;
  const toggleDetails = (awardKey: string) => setExpanded((current) => {
    const next = new Set(current);
    if (next.has(awardKey)) next.delete(awardKey);
    else next.add(awardKey);
    return next;
  });

  return (
    <Box
      component="section"
      aria-labelledby={embedded ? undefined : 'previous-federal-awards-heading'}
      sx={{ mt: embedded ? 0 : 2 }}
    >
      {!embedded && <Typography id="previous-federal-awards-heading" variant="h5" component="h2" sx={{ mb: 1.5 }}>Previous federal awards</Typography>}
          {noResults ? (
            <Alert severity="info">
              Search complete. No comparable federal award totals were found for the current project details.
            </Alert>
          ) : failed ? (
            <Alert severity={status === 'not_authorized' ? 'warning' : 'info'}>
              {retryMessage ?? 'USAspending is not available for this price search.'}
            </Alert>
          ) : status === 'not_configured' && items.length === 0 ? (
            <Alert severity="info">USAspending is unavailable for this price search.</Alert>
          ) : items.length === 0 ? (
            <Alert severity="info">Award search information is not available for this saved result.</Alert>
          ) : (
            <Box>
          {awardEvidenceIsContextOnly ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" component="div">Context-only award evidence</Typography>
              USAspending returned these historical award totals for review. They are not used as an award-based planning total or range.
            </Alert>
          ) : comparableCohort ? (
            <Alert severity={comparableCohort.status === 'tight_supported' || comparableCohort.status === 'broader_supported' ? 'info' : 'warning'} sx={{ mb: 2 }}>
              {comparableCohort.summary}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              {isRom
              ? 'These historical award totals support a limited planning range. They are not unit prices, an itemized estimate, or an IGE.'
              : 'Reported award totals are historical reference prices and do not determine an itemized estimate.'}
            </Alert>
          )}
          {comparableCohort && <Typography variant="body2" sx={{ mb: 2 }}>
            {comparableCohort.selected_count} {comparableCohort.selected_count === 1 ? 'award was' : 'awards were'} selected for comparison
            {comparableCohort.geographic_tier ? ` from the saved ${comparableCohort.geographic_tier} area` : ''}
            {comparableCohort.excluded_count ? `; ${comparableCohort.excluded_count} ${comparableCohort.excluded_count === 1 ? 'award was' : 'awards were'} not used.` : '.'}
          </Typography>}
          {includedCount === 1 && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              One compatible award was found. Its reported total is a historical reference; at least three compatible awards are needed for a planning range.
            </Typography>
          )}
          {displayUserMessages(warnings).map((warning) => <Alert key={warning} severity="warning" sx={{ mb: 1 }}>{warning}</Alert>)}
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Previous federal awards" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Award</TableCell>
                  <TableCell>What was purchased</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>County</TableCell>
                  <TableCell>Award date</TableCell>
                  <TableCell align="right">Reported award total</TableCell>
                  <TableCell>Selection</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => {
                  const awardId = String(item.award_id ?? 'Award identifier not reported');
                  const key = canonicalAwardKey(item) || awardId;
                  const detailsId = `award-details-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
                  const isExpanded = expanded.has(key);
                  const publicUrl = permittedUSAspendingUrl(item.award_url);
                  const rationale = rationales[awardId] ?? '';
                  const adjustedAmount = item.normalized_amount ?? item.adjusted_amount ?? item.escalated_amount;
                  const hasAdjustedAmount = adjustedAmount !== null && adjustedAmount !== undefined
                    && String(adjustedAmount) !== String(item.original_amount);
                  return (
                    <React.Fragment key={key}>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ minWidth: 170 }}>
                          {publicUrl
                            ? <a href={publicUrl} target="_blank" rel="noreferrer" aria-label={`Open USAspending award ${awardId}`}>{awardId}</a>
                            : awardId}
                          <Typography variant="caption" display="block">Agency: {String(item.awarding_agency ?? 'Not reported')}</Typography>
                          <Typography variant="caption" display="block">Recipient: {String(item.recipient ?? 'Not reported')}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 260 }}>
                          <Typography variant="body2">{String(item.description ?? 'Description not reported')}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">{comparableBasis(item)}</Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{String(item.place_of_performance_state ?? item.place_of_performance ?? 'Not reported')}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{String(item.place_of_performance_city ?? 'Not reported')}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{String(item.place_of_performance_county ?? 'Not reported')}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{date(item.action_date)}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{money(item.original_amount)}</TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block">
                            {item.semantic_outcome === 'direct_comparable'
                              ? String(item.semantic_label ?? 'Semantically matched work package')
                              : item.semantic_outcome === 'context_only'
                                ? String(item.semantic_label ?? 'Shown as award context only')
                                : item.included !== false
                                  ? 'Used for comparison'
                                  : displayBackendValue(item.exclusion_label, 'Not used for comparison')}
                          </Typography>
                          <Button
                            size="small"
                            aria-expanded={isExpanded}
                            aria-controls={detailsId}
                            onClick={() => toggleDetails(key)}
                          >
                            View details
                          </Button>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow id={detailsId}>
                          <TableCell colSpan={8} sx={{ bgcolor: 'action.hover' }}>
                            <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'max-content 1fr' }, gap: 0.75, m: 0, mb: 1.5 }}>
                              <Typography component="dt" variant="body2" fontWeight={700}>Amount meaning</Typography>
                              <Typography component="dd" variant="body2" sx={{ m: 0 }}>{displayBackendValue(item.amount_meaning, 'Historical source amount')}</Typography>
                              {hasAdjustedAmount && <><Typography component="dt" variant="body2" fontWeight={700}>Amount used for comparison</Typography><Typography component="dd" variant="body2" sx={{ m: 0 }}>{money(adjustedAmount)}</Typography></>}
                              {hasAdjustedAmount && <><Typography component="dt" variant="body2" fontWeight={700}>Adjustment</Typography><Typography component="dd" variant="body2" sx={{ m: 0 }}>{displayBackendValue(item.normalization_basis ?? item.normalization_factor, 'Adjusted using the approved pricing method.')}</Typography></>}
                              {item.naics && <><Typography component="dt" variant="body2" fontWeight={700}>NAICS</Typography><Typography component="dd" variant="body2" sx={{ m: 0 }}>{String(item.naics)}</Typography></>}
                              {item.psc && <><Typography component="dt" variant="body2" fontWeight={700}>PSC</Typography><Typography component="dd" variant="body2" sx={{ m: 0 }}>{String(item.psc)}</Typography></>}
                              {item.retrieved_at && <><Typography component="dt" variant="body2" fontWeight={700}>Retrieved</Typography><Typography component="dd" variant="body2" sx={{ m: 0 }}>{date(item.retrieved_at)}</Typography></>}
                            </Box>
                            {canEdit && item.included !== false && (
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                                <ExternalField id={`exclude-award-${key}`} label="Reason to exclude" sx={{ minWidth: 280 }}>
                                  <TextField
                                    id={`exclude-award-${key}`}
                                    size="small"
                                    value={rationale}
                                    disabled={saving}
                                    inputProps={{ 'aria-label': `Reason to exclude ${awardId}` }}
                                    onChange={(event) => onRationaleChange(awardId, event.target.value)}
                                    fullWidth
                                  />
                                </ExternalField>
                                <Button
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  disabled={saving || !rationale.trim()}
                                  onClick={() => onExclude(awardId)}
                                  aria-label={`Exclude from planning range ${awardId}`}
                                >
                                  Exclude from planning range
                                </Button>
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
            </Box>
          )}
    </Box>
  );
};
